import React from "react";
import logo from "assets/images/occ-logo-white.png";
import { useAppDispatch, useAppSelector } from "common/hooks/storeHooks";
import { Nullable } from "common/types";
import { currentUserOrgId } from "common/selectors/user.selectors";
import { HamburgerItem, HamburgerItemType } from "common/components/HamburgerMenu/HamburgerItem";
import { hamburgerMenu } from "common/selectors/common.selectors";
import { CommonActions } from "common/reducers/common.actions";
import { currentUserOrgDescendantsCount } from "common/selectors/org.selectors";

export const hamburgerMenuItems = (orgId: Nullable<number>): HamburgerItemType[] => {
  return [
    {
      name: "Customers",
      path: "/jobs",
      icon: "apps",
      permissions: ["header_panel", "jobs"],
      closeOnClick: true,
    },
    {
      name: "Organizations",
      path: "/orgs",
      icon: "admin_panel_settings",
      showSelector: (state) => currentUserOrgDescendantsCount(state) >= 1,
      closeOnClick: true,
    },
    {
      name: "Payments",
      path: "/payments",
      icon: "payments",
      permissions: ["header_panel", "payments"],
      closeOnClick: true,
    },
    {
      name: "Calendar",
      path: "/calendar",
      icon: "calendar_today",
      permissions: ["header_panel", "calendar"],
      closeOnClick: true,
    },
    {
      name: "Finance",
      path: "/finance",
      icon: "finance",
      closeOnClick: true,
    },
    {
      name: "Configuration",
      path: `/orgs/${orgId}/info`,
      icon: "settings",
      permissions: ["configuration", "link"],
      closeOnClick: true,
    },
    {
      name: "System Settings",
      path: "/system_settings",
      icon: "build",
      permissions: ["global", "org"],
      closeOnClick: true,
    },
    {
      name: "Training Material",
      icon: "local_library",
      path: "https://help.oneclickcontractor.com/knowledge",
      closeOnClick: true,
    },
  ];
};

export const HamburgerMenu: React.FC = () => {
  // Hooks
  const dispatch = useAppDispatch();

  // Local State
  const componentRef = React.useRef<HTMLDivElement>(null);

  // Selectors
  const orgId = useAppSelector(currentUserOrgId);
  const isOpen = useAppSelector(hamburgerMenu);

  // Methods
  const close = () => {
    dispatch(CommonActions.setHamburgerMenu(false));
  };

  const handleClickOutside = (event: any) => {
    if (componentRef && componentRef.current && !componentRef.current.contains(event.target)) {
      close();
    }
  };

  // Lifecycle
  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      id="hamburger-menu"
      className={`fixed inset-y-0 left-0 bg-gray-800 text-white w-[235px] h-full transform transition duration-300 ease-in-out ${
        isOpen ? "" : "-translate-x-full"
      }`}
      ref={componentRef}
      style={{ zIndex: 99999999999 }}>
      <div className="p-lg">
        <div className="flex justify-between pb-lg">
          <img src={logo} alt="One Click Contractor" />
          <span className="material-symbols-outlined text-[24px] cursor-pointer" onClick={close}>
            menu_open
          </span>
        </div>

        <div className="space-y-sm">
          {hamburgerMenuItems(orgId).map((item, idx) => (
            <HamburgerItem item={item} key={idx} />
          ))}
        </div>
      </div>
    </div>
  );
};
